import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  DatePicker,
  Button,
  Tabs,
  Card,
  Row,
  Col,
  Space,
  message,
  Table,
  InputNumber,
  Typography,
  Divider,
  Tag,
  Tooltip,
  Alert,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  DollarOutlined,
  PercentageOutlined,
  BankOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  HRTaxLegislation,
  TaxLegislationFormData,
  SocialContributionFormData,
  TaxBracketFormData,
  TaxAllowanceFormData,
  LegislationParameterFormData,
} from "../../types/hr.d";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface TaxLegislationFormProps {
  open: boolean;
  disabled?: boolean;
  companyId?: number;
  errorMessage?: string | null;
  onClose: () => void;
  onSuccess: () => void;
  legislation?: HRTaxLegislation | null;
}

const TaxLegislationForm: React.FC<TaxLegislationFormProps> = ({
  open,
  disabled = false,
  companyId,
  errorMessage,
  onClose,
  onSuccess,
  legislation,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  // États pour les éléments associés
  const [socialContributions, setSocialContributions] = useState<
    SocialContributionFormData[]
  >([]);
  const [taxBrackets, setTaxBrackets] = useState<TaxBracketFormData[]>([]);
  const [taxAllowances, setTaxAllowances] = useState<TaxAllowanceFormData[]>(
    []
  );
  const [parameters, setParameters] = useState<LegislationParameterFormData[]>(
    []
  );

  const isEditing = !!legislation;

  // Initialisation du formulaire
  useEffect(() => {
    if (legislation) {
      form.setFieldsValue({
        country_code: legislation.country_code,
        country_name: legislation.country_name,
        currency_code: legislation.currency_code,
        currency_symbol: legislation.currency_symbol,
        is_default: legislation.is_default,
        effective_date: dayjs(legislation.effective_date),
        description: legislation.description,
        is_active: legislation.is_active,
      });

      // Charger les éléments associés
      if (legislation.social_contributions) {
        setSocialContributions(legislation.social_contributions);
      }
      if (legislation.tax_brackets) {
        setTaxBrackets(legislation.tax_brackets);
      }
      if (legislation.tax_allowances) {
        setTaxAllowances(legislation.tax_allowances);
      }
      if (legislation.parameters) {
        setParameters(legislation.parameters);
      }
    } else {
      form.resetFields();
      setSocialContributions([]);
      setTaxBrackets([]);
      setTaxAllowances([]);
      setParameters([]);
    }
  }, [legislation, form]);

  // Gestionnaire de soumission
  const handleSubmit = async (values: any) => {
    if (disabled) return;
    setLoading(true);

    try {
      const formData: TaxLegislationFormData = {
        country_code: values.country_code,
        country_name: values.country_name,
        currency_code: values.currency_code,
        currency_symbol: values.currency_symbol,
        is_default: values.is_default || false,
        effective_date: values.effective_date.format("YYYY-MM-DD"),
        description: values.description,
        is_active: values.is_active !== undefined ? values.is_active : true,
        social_contributions: socialContributions,
        tax_brackets: taxBrackets,
        tax_allowances: taxAllowances,
        parameters: parameters,
      };

      const url = isEditing
        ? `/api/hr/tax-legislations/${legislation!.id}`
        : "/api/hr/tax-legislations";

      const method = isEditing ? "PUT" : "POST";

      const token = localStorage.getItem("authToken");
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(companyId ? { "X-Company-Id": String(companyId) } : {}),
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        message.success(
          isEditing
            ? "Législation mise à jour avec succès"
            : "Législation créée avec succès"
        );
        onSuccess();
      } else {
        message.error(data.message || "Erreur lors de la sauvegarde");
      }
    } catch (error) {
      console.error("Erreur:", error);
      message.error("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  // Gestionnaires pour les cotisations sociales
  const addSocialContribution = () => {
    const newContribution: SocialContributionFormData = {
      code: "",
      name: "",
      employee_rate: 0,
      employer_rate: 0,
      min_base: 0,
      calculation_base: "gross_salary",
      is_mandatory: true,
      display_order: socialContributions.length + 1,
      is_active: true,
    };
    setSocialContributions([...socialContributions, newContribution]);
  };

  const updateSocialContribution = (
    index: number,
    field: keyof SocialContributionFormData,
    value: any
  ) => {
    const updated = [...socialContributions];
    updated[index] = { ...updated[index], [field]: value };
    setSocialContributions(updated);
  };

  const deleteSocialContribution = (index: number) => {
    setSocialContributions(socialContributions.filter((_, i) => i !== index));
  };

  // Gestionnaires pour les tranches d'imposition
  const addTaxBracket = () => {
    const newBracket: TaxBracketFormData = {
      bracket_order: taxBrackets.length + 1,
      min_amount: 0,
      rate: 0,
      fixed_amount: 0,
      is_active: true,
    };
    setTaxBrackets([...taxBrackets, newBracket]);
  };

  const updateTaxBracket = (
    index: number,
    field: keyof TaxBracketFormData,
    value: any
  ) => {
    const updated = [...taxBrackets];
    updated[index] = { ...updated[index], [field]: value };
    setTaxBrackets(updated);
  };

  const deleteTaxBracket = (index: number) => {
    setTaxBrackets(taxBrackets.filter((_, i) => i !== index));
  };

  // Gestionnaires pour les abattements
  const addTaxAllowance = () => {
    const newAllowance: TaxAllowanceFormData = {
      code: "",
      name: "",
      type: "personal",
      amount: 0,
      is_percentage: false,
      is_active: true,
    };
    setTaxAllowances([...taxAllowances, newAllowance]);
  };

  const updateTaxAllowance = (
    index: number,
    field: keyof TaxAllowanceFormData,
    value: any
  ) => {
    const updated = [...taxAllowances];
    updated[index] = { ...updated[index], [field]: value };
    setTaxAllowances(updated);
  };

  const deleteTaxAllowance = (index: number) => {
    setTaxAllowances(taxAllowances.filter((_, i) => i !== index));
  };

  // Colonnes pour les cotisations sociales
  const socialContributionColumns: ColumnsType<SocialContributionFormData> = [
    {
      title: "Code",
      key: "code",
      width: 100,
      render: (_, record, index) => (
        <Input
          value={record.code}
          onChange={(e) =>
            updateSocialContribution(index, "code", e.target.value)
          }
          placeholder="Ex: CNSS"
        />
      ),
    },
    {
      title: "Nom",
      key: "name",
      width: 200,
      render: (_, record, index) => (
        <Input
          value={record.name}
          onChange={(e) =>
            updateSocialContribution(index, "name", e.target.value)
          }
          placeholder="Ex: Caisse Nationale de Sécurité Sociale"
        />
      ),
    },
    {
      title: "Taux employé (%)",
      key: "employee_rate",
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={record.employee_rate}
          onChange={(value) =>
            updateSocialContribution(index, "employee_rate", value || 0)
          }
          min={0}
          max={100}
          step={0.1}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Taux employeur (%)",
      key: "employer_rate",
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={record.employer_rate}
          onChange={(value) =>
            updateSocialContribution(index, "employer_rate", value || 0)
          }
          min={0}
          max={100}
          step={0.1}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Base min",
      key: "min_base",
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={record.min_base}
          onChange={(value) =>
            updateSocialContribution(index, "min_base", value || 0)
          }
          min={0}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Plafond",
      key: "max_base",
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={record.max_base}
          onChange={(value) =>
            updateSocialContribution(index, "max_base", value)
          }
          min={0}
          placeholder="Illimité"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => deleteSocialContribution(index)}
        />
      ),
    },
  ];

  // Colonnes pour les tranches d'imposition
  const taxBracketColumns: ColumnsType<TaxBracketFormData> = [
    {
      title: "Ordre",
      key: "bracket_order",
      width: 80,
      render: (_, record, index) => (
        <InputNumber
          value={record.bracket_order}
          onChange={(value) =>
            updateTaxBracket(index, "bracket_order", value || 1)
          }
          min={1}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Montant min",
      key: "min_amount",
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={record.min_amount}
          onChange={(value) =>
            updateTaxBracket(index, "min_amount", value || 0)
          }
          min={0}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Montant max",
      key: "max_amount",
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={record.max_amount}
          onChange={(value) => updateTaxBracket(index, "max_amount", value)}
          min={0}
          placeholder="Illimité"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Taux (%)",
      key: "rate",
      width: 100,
      render: (_, record, index) => (
        <InputNumber
          value={record.rate}
          onChange={(value) => updateTaxBracket(index, "rate", value || 0)}
          min={0}
          max={100}
          step={0.1}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Description",
      key: "description",
      width: 200,
      render: (_, record, index) => (
        <Input
          value={record.description}
          onChange={(e) =>
            updateTaxBracket(index, "description", e.target.value)
          }
          placeholder="Ex: Première tranche"
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => deleteTaxBracket(index)}
        />
      ),
    },
  ];

  // Colonnes pour les abattements
  const taxAllowanceColumns: ColumnsType<TaxAllowanceFormData> = [
    {
      title: "Code",
      key: "code",
      width: 100,
      render: (_, record, index) => (
        <Input
          value={record.code}
          onChange={(e) => updateTaxAllowance(index, "code", e.target.value)}
          placeholder="Ex: ABAT_PERS"
        />
      ),
    },
    {
      title: "Nom",
      key: "name",
      width: 200,
      render: (_, record, index) => (
        <Input
          value={record.name}
          onChange={(e) => updateTaxAllowance(index, "name", e.target.value)}
          placeholder="Ex: Abattement personnel"
        />
      ),
    },
    {
      title: "Type",
      key: "type",
      width: 120,
      render: (_, record, index) => (
        <Select
          value={record.type}
          onChange={(value) => updateTaxAllowance(index, "type", value)}
          style={{ width: "100%" }}
        >
          <Option value="personal">Personnel</Option>
          <Option value="family">Familial</Option>
          <Option value="professional">Professionnel</Option>
          <Option value="other">Autre</Option>
        </Select>
      ),
    },
    {
      title: "Montant",
      key: "amount",
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={record.amount}
          onChange={(value) => updateTaxAllowance(index, "amount", value || 0)}
          min={0}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Max bénéficiaires",
      key: "max_beneficiaries",
      width: 120,
      render: (_, record, index) => (
        <InputNumber
          value={record.max_beneficiaries}
          onChange={(value) =>
            updateTaxAllowance(index, "max_beneficiaries", value)
          }
          min={1}
          placeholder="Illimité"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => deleteTaxAllowance(index)}
        />
      ),
    },
  ];

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <GlobalOutlined />
          {isEditing
            ? "Modifier la législation fiscale"
            : "Nouvelle législation fiscale"}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={1200}
      style={{ top: 20 }}
    >
      {errorMessage && <Alert type="error" showIcon message="Sauvegarde bloquée" description={errorMessage} style={{ marginBottom: 16 }} />}
      <Form
        form={form}
        disabled={disabled}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          is_active: true,
          is_default: false,
        }}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* Informations de base */}
          <TabPane tab="Informations de base" key="basic">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Code pays (ISO)"
                  name="country_code"
                  rules={[
                    { required: true, message: "Code pays obligatoire" },
                    { len: 3, message: "Le code doit faire 3 caractères" },
                  ]}
                >
                  <Input
                    placeholder="Ex: BFA, CIV, SEN"
                    style={{ textTransform: "uppercase" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Nom du pays"
                  name="country_name"
                  rules={[
                    { required: true, message: "Nom du pays obligatoire" },
                  ]}
                >
                  <Input placeholder="Ex: Burkina Faso" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Code devise"
                  name="currency_code"
                  rules={[
                    { required: true, message: "Code devise obligatoire" },
                    { len: 3, message: "Le code doit faire 3 caractères" },
                  ]}
                >
                  <Input
                    placeholder="Ex: XOF, EUR, USD"
                    style={{ textTransform: "uppercase" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Symbole devise"
                  name="currency_symbol"
                  rules={[
                    { required: true, message: "Symbole devise obligatoire" },
                  ]}
                >
                  <Input placeholder="Ex: FCFA, €, $" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Date d'entrée en vigueur"
                  name="effective_date"
                  rules={[
                    { required: true, message: "Date effective obligatoire" },
                  ]}
                >
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    alignItems: "center",
                    marginTop: 30,
                  }}
                >
                  <Form.Item
                    name="is_default"
                    valuePropName="checked"
                    style={{ margin: 0 }}
                  >
                    <Switch
                      checkedChildren="Défaut"
                      unCheckedChildren="Standard"
                    />
                  </Form.Item>
                  <Form.Item
                    name="is_active"
                    valuePropName="checked"
                    style={{ margin: 0 }}
                  >
                    <Switch
                      checkedChildren="Actif"
                      unCheckedChildren="Inactif"
                    />
                  </Form.Item>
                </div>
              </Col>
            </Row>

            <Form.Item label="Description" name="description">
              <TextArea
                rows={3}
                placeholder="Description de la législation..."
              />
            </Form.Item>
          </TabPane>

          {/* Cotisations sociales */}
          <TabPane tab="Cotisations sociales" key="social">
            <div style={{ marginBottom: 16 }}>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addSocialContribution}
                style={{ width: "100%" }}
              >
                Ajouter une cotisation sociale
              </Button>
            </div>

            <Table
              columns={socialContributionColumns}
              dataSource={socialContributions}
              rowKey={(record, index) => index!}
              pagination={false}
              scroll={{ x: 800 }}
              size="small"
            />
          </TabPane>

          {/* Tranches d'imposition */}
          <TabPane tab="Tranches d'imposition" key="brackets">
            <div style={{ marginBottom: 16 }}>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addTaxBracket}
                style={{ width: "100%" }}
              >
                Ajouter une tranche d'imposition
              </Button>
            </div>

            <Table
              columns={taxBracketColumns}
              dataSource={taxBrackets}
              rowKey={(record, index) => index!}
              pagination={false}
              scroll={{ x: 800 }}
              size="small"
            />
          </TabPane>

          {/* Abattements */}
          <TabPane tab="Abattements" key="allowances">
            <div style={{ marginBottom: 16 }}>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addTaxAllowance}
                style={{ width: "100%" }}
              >
                Ajouter un abattement
              </Button>
            </div>

            <Table
              columns={taxAllowanceColumns}
              dataSource={taxAllowances}
              rowKey={(record, index) => index!}
              pagination={false}
              scroll={{ x: 800 }}
              size="small"
            />
          </TabPane>
        </Tabs>

        <Divider />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>Annuler</Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {isEditing ? "Mettre à jour" : "Créer"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default TaxLegislationForm;
